import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { reviewsApi } from "./client";

export const reviewKeys = {
  all: ["reviews"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["reviews", "list", q] as const,
  detail: (id: string) => ["reviews", "detail", id] as const,
};

export function useReviewsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: reviewKeys.list(q),
    queryFn: () => reviewsApi.list(q),
  });
}

export function useReviewQuery(id: string) {
  return useQuery({
    queryKey: reviewKeys.detail(id),
    queryFn: () => reviewsApi.get(id),
    enabled: !!id,
  });
}

export function useUpdateReviewHidden() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, hidden }: { id: string; hidden: boolean }) =>
      reviewsApi.updateHidden(id, hidden),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: reviewKeys.detail(id) });
      qc.invalidateQueries({ queryKey: reviewKeys.all });
    },
  });
}

export function useDeleteReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reviewsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reviewKeys.all });
    },
  });
}
